import { gql } from "@apollo/client";
import tzlookup from "tz-lookup";

import { Location } from "../../API";
import client from "../../configs/apolloClient";

/**
 * Core timezone resolution logic - shared between hook and direct cache access.
 * Derives IANA timezone from location's lat/lon coordinates.
 * @param {Location | null} location - The location object from Apollo cache
 * @return {string | null} IANA timezone string or null if unavailable
 */
export function resolveTimezoneFromLocation(
  location: Location | null
): string | null {
  if (!location) return null;

  if (!location.locationData) return null;

  try {
    const data = JSON.parse(location.locationData);
    const lat = Number(data.lat);
    const lon = Number(data.lon);

    if (isNaN(lat) || isNaN(lon)) return null;

    return tzlookup(lat, lon);
  } catch {
    return null;
  }
}

/**
 * Non-hook accessor for use in table cell renderers.
 * Gets location from Apollo cache and resolves timezone.
 * @param {string | undefined} locationId - The location ID to look up
 * @return {string | null} IANA timezone string or null if unavailable
 */
export function getTimezoneFromLocationCache(
  locationId: string | undefined
): string | null {
  if (!locationId) return null;

  const normalizedId =
    client.cache.identify({
      id: locationId,
      __typename: "Location",
    }) ?? "";

  const location = client.cache.readFragment<Location>({
    id: normalizedId,
    fragment: gql`
      fragment LocationTimezoneFragment on Location {
        id
        locationData
      }
    `,
  });

  return resolveTimezoneFromLocation(location);
}
